import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  GoogleGenAI,
  type LiveServerMessage,
  Modality,
  StartSensitivity,
  EndSensitivity,
} from '@google/genai';
import { createBlob, decode, decodeAudioData } from '@/tutor/audioUtils';
import { pickSendClientContent } from '@/tutor/genaiLiveHelpers';
import type { PictureTalkSceneDetail, PictureTalkSceneItem } from '@/modules/learning/api/pictureTalk';
import { isSchoolFeatureDisabledHandledError } from '@/utils/schoolFeatureDisabledStudent';
import { isAuthSessionExpiredHandledError } from '@/utils/tutorSessionTokenAuth';
import { useStudentMe } from '@/modules/roster/hooks/useStudentMe';
import { buildPictureTalkObjectClickedEvent, buildPictureTalkStartEvent } from './pictureTalkLiveEvents';
import { fetchPictureTalkLiveToken } from './pictureTalkLiveToken';
import { PICTURE_TALK_STANDARD_DIALECT_KEY } from './pictureTalkDialectKeys';
import {
  buildPictureTalkResolverSceneContext,
  PICTURE_TALK_EMPTY_FINAL_PROMPT,
  resolvePictureTalkLiveSystemInstruction,
} from './resolvePictureTalkLiveSystemInstruction';

const MODEL = 'gemini-2.5-flash-native-audio-preview-09-2025';
const VOICE = 'Erinome';
const INPUT_AUDIO_CONTEXT_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
const SCRIPT_PROCESSOR_BUFFER_SIZE = 2048;
const CLIENT_VAD_SILENCE_RMS = 0.005;
const CLIENT_VAD_SILENCE_MS = 800;

export type PictureTalkLivePhase = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

export type UsePictureTalkLiveSessionOptions = {
  navigate: NavigateFunction;
  refetchFeatureAccess: () => Promise<void>;
  sceneId: string;
  /** Dialect key selected by the user (e.g. 'eelam_tamil', 'standard_tamil'). */
  dialectKey?: string | null;
};

export function usePictureTalkLiveSession(options: UsePictureTalkLiveSessionOptions) {
  const {
    navigate,
    refetchFeatureAccess,
    sceneId,
    dialectKey: rawDialectKey,
  } = options;
  const dialectKey = rawDialectKey?.trim() || PICTURE_TALK_STANDARD_DIALECT_KEY;

  const { grade: studentGrade } = useStudentMe();

  const [phase, setPhase] = useState<PictureTalkLivePhase>('idle');
  const [isReady, setIsReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastSent, setLastSent] = useState('');
  const [lastReply, setLastReply] = useState('');
  const [lastModelText, setLastModelText] = useState('');

  const connectLockRef = useRef(false);
  const generationRef = useRef(0);
  const sessionPromiseRef = useRef<Promise<unknown> | null>(null);
  const liveSessionRef = useRef<unknown>(null);
  const sendTextRef = useRef<((text: string) => Promise<void>) | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTimeRef = useRef(0);
  const didOpenRef = useRef(false);
  const intentionalEndRef = useRef(false);
  const isMutedRef = useRef(false);
  const isTeacherSpeakingRef = useRef(false);
  const clientSilenceMsRef = useRef(0);
  const clientVadEndSentRef = useRef(false);
  const pendingClickFlushRef = useRef<number | null>(null);
  const pendingSceneRef = useRef<PictureTalkSceneDetail | null>(null);
  const pendingItemRef = useRef<PictureTalkSceneItem | null>(null);
  const phaseRef = useRef<PictureTalkLivePhase>('idle');
  const isReadyRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    isReadyRef.current = isReady;
  }, [isReady]);

  const interruptOutput = useCallback(() => {
    sourcesRef.current.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* ignore */
      }
    });
    sourcesRef.current.clear();
    const ctx = audioContextRef.current;
    if (ctx) nextStartTimeRef.current = ctx.currentTime;
  }, []);

  const cleanupAudioAndMic = useCallback(async () => {
    interruptOutput();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (processorRef.current) {
      try {
        processorRef.current.disconnect();
      } catch {
        /* ignore */
      }
      processorRef.current = null;
    }
    if (inputSourceRef.current) {
      try {
        inputSourceRef.current.disconnect();
      } catch {
        /* ignore */
      }
      inputSourceRef.current = null;
    }
    const inCtx = inputAudioContextRef.current;
    const outCtx = audioContextRef.current;
    if (inCtx && inCtx.state !== 'closed') {
      try {
        await inCtx.close();
      } catch {
        /* ignore */
      }
    }
    if (outCtx && outCtx.state !== 'closed') {
      try {
        await outCtx.close();
      } catch {
        /* ignore */
      }
    }
    inputAudioContextRef.current = null;
    audioContextRef.current = null;
    sendTextRef.current = null;
    liveSessionRef.current = null;
    sessionPromiseRef.current = null;
    didOpenRef.current = false;
    setIsReady(false);
  }, [interruptOutput]);

  const endConversation = useCallback(async () => {
    intentionalEndRef.current = true;
    interruptOutput();
    const sess = liveSessionRef.current as { close?: () => Promise<void> } | null;
    try {
      if (sess?.close) await sess.close();
    } catch {
      /* ignore */
    }
    await cleanupAudioAndMic();
    sessionPromiseRef.current = null;
    liveSessionRef.current = null;
    setPhase((p) => (p === 'error' ? p : 'ended'));
    connectLockRef.current = false;
  }, [cleanupAudioAndMic, interruptOutput]);

  const prevSceneIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevSceneIdRef.current !== undefined && prevSceneIdRef.current !== sceneId) {
      void endConversation();
      setPhase('idle');
      setErrorMessage(null);
      setLastSent('');
      setLastReply('');
      setLastModelText('');
    }
    prevSceneIdRef.current = sceneId;
  }, [sceneId, endConversation]);

  useEffect(() => {
    return () => {
      if (pendingClickFlushRef.current) {
        window.clearTimeout(pendingClickFlushRef.current);
        pendingClickFlushRef.current = null;
      }
      void endConversation();
    };
  }, [endConversation]);

  const flushPendingObjectClick = useCallback(async () => {
    pendingClickFlushRef.current = null;
    const scene = pendingSceneRef.current;
    const item = pendingItemRef.current;
    pendingItemRef.current = null;
    if (!scene || !item) return;
    if (phaseRef.current !== 'connected' || !isReadyRef.current) return;
    const send = sendTextRef.current;
    if (!send) return;
    const gen = generationRef.current;
    try {
      interruptOutput();
      const payload = buildPictureTalkObjectClickedEvent(scene, item);
      setLastSent(`OBJECT_CLICKED: ${item.id}`);
      await send(payload);
    } catch {
      if (generationRef.current === gen) {
        setErrorMessage('Failed to send tap to tutor.');
      }
    }
  }, [interruptOutput]);

  const notifyItemClicked = useCallback(
    (item: PictureTalkSceneItem, scene: PictureTalkSceneDetail) => {
      pendingSceneRef.current = scene;
      pendingItemRef.current = item;
      if (pendingClickFlushRef.current) window.clearTimeout(pendingClickFlushRef.current);
      pendingClickFlushRef.current = window.setTimeout(() => {
        void flushPendingObjectClick();
      }, 0);
    },
    [flushPendingObjectClick]
  );

  const startConversation = useCallback(
    async (scene: PictureTalkSceneDetail, selectedItem?: PictureTalkSceneItem | null) => {
      if (connectLockRef.current) return;
      connectLockRef.current = true;
      intentionalEndRef.current = false;
      generationRef.current += 1;
      const gen = generationRef.current;
      setErrorMessage(null);
      setPhase('connecting');
      setIsReady(false);
      setLastReply('');
      setLastModelText('');

      try {
        await cleanupAudioAndMic();
        if (generationRef.current !== gen) {
          connectLockRef.current = false;
          return;
        }

        let systemInstruction: string;
        try {
          const sceneContext = buildPictureTalkResolverSceneContext(scene);
          const gradeForLog =
            studentGrade != null && Number.isFinite(Number(studentGrade))
              ? Number(studentGrade)
              : undefined;
          if (import.meta.env.DEV) {
            console.log('[PictureTalkLive] resolving prompt', {
              grade: gradeForLog,
              dialectKey,
              eventType: 'start',
              hasSceneContext: Boolean(sceneContext?.trim()),
            });
          }
          const resolved = await resolvePictureTalkLiveSystemInstruction({
            scene,
            dialectKey,
            studentGrade,
            selectedItem,
          });
          systemInstruction = resolved.systemInstruction;
          if (import.meta.env.DEV) {
            console.log('[PictureTalkLive] prompt resolved', {
              selectedTemplateVersion: resolved.promptRes.selected_template_version,
              resolvedDialectKey: resolved.promptRes.resolved_dialect_key,
              finalPromptLength: resolved.systemInstruction.length,
            });
          }
        } catch (e) {
          if (import.meta.env.DEV) {
            console.warn('[PictureTalkLive] prompt resolver failed', e);
          }
          if (e instanceof Error && e.message === PICTURE_TALK_EMPTY_FINAL_PROMPT) {
            setErrorMessage('Tutor instructions were empty. Please try again or contact support.');
          } else {
            setErrorMessage('Could not load tutor instructions. Please try again.');
          }
          setPhase('error');
          connectLockRef.current = false;
          return;
        }

        const settled = await Promise.allSettled([
          fetchPictureTalkLiveToken({ navigate, refetchFeatureAccess }),
          navigator.mediaDevices.getUserMedia({ audio: true }),
        ]);

        if (generationRef.current !== gen) {
          if (settled[1].status === 'fulfilled') {
            settled[1].value.getTracks().forEach((t) => t.stop());
          }
          connectLockRef.current = false;
          return;
        }

        const tokenRes = settled[0];
        const micRes = settled[1];
        if (tokenRes.status !== 'fulfilled') {
          if (micRes.status === 'fulfilled') micRes.value.getTracks().forEach((t) => t.stop());
          if (
            isSchoolFeatureDisabledHandledError(tokenRes.reason) ||
            isAuthSessionExpiredHandledError(tokenRes.reason)
          ) {
            setPhase('idle');
            connectLockRef.current = false;
            return;
          }
          const msg =
            tokenRes.reason instanceof Error ? tokenRes.reason.message : 'Could not get session token.';
          setErrorMessage(msg);
          setPhase('error');
          connectLockRef.current = false;
          return;
        }
        if (micRes.status !== 'fulfilled') {
          const name = (micRes.reason as Error)?.name;
          setErrorMessage(
            name === 'NotAllowedError'
              ? 'Microphone permission is required to start the conversation.'
              : 'Could not access the microphone.'
          );
          setPhase('error');
          connectLockRef.current = false;
          return;
        }

        const token = tokenRes.value;
        const stream = micRes.value;
        streamRef.current = stream;

        const AudioClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const outputCtx = new AudioClass({ sampleRate: OUTPUT_SAMPLE_RATE });
        const inputCtx = new AudioClass({
          sampleRate: INPUT_AUDIO_CONTEXT_RATE,
          latencyHint: 'interactive',
        });
        audioContextRef.current = outputCtx;
        inputAudioContextRef.current = inputCtx;
        if (outputCtx.state === 'suspended') await outputCtx.resume();
        if (inputCtx.state === 'suspended') await inputCtx.resume();

        if (generationRef.current !== gen) {
          stream.getTracks().forEach((t) => t.stop());
          await outputCtx.close();
          await inputCtx.close();
          audioContextRef.current = null;
          inputAudioContextRef.current = null;
          connectLockRef.current = false;
          return;
        }

        const ai = new GoogleGenAI({
          apiKey: token,
          httpOptions: { apiVersion: 'v1alpha' },
        });

        const sp = ai.live.connect({
          model: MODEL,
          config: {
            systemInstruction,
            responseModalities: [Modality.AUDIO],
            thinkingConfig: { thinkingBudget: 0 },
            inputAudioTranscription: {},
            outputAudioTranscription: {},
            speechConfig: {
              voiceConfig: { prebuiltVoiceConfig: { voiceName: VOICE } },
            },
            realtimeInputConfig: {
              automaticActivityDetection: {
                startOfSpeechSensitivity: StartSensitivity.START_SENSITIVITY_HIGH,
                endOfSpeechSensitivity: EndSensitivity.END_SENSITIVITY_HIGH,
                prefixPaddingMs: 20,
                silenceDurationMs: CLIENT_VAD_SILENCE_MS,
              },
            },
          },
          callbacks: {
            onopen: () => {
              if (generationRef.current !== gen) return;
              didOpenRef.current = true;
              setPhase('connected');
              void sp.then(async (sess: unknown) => {
                if (generationRef.current !== gen) return;
                liveSessionRef.current = sess;
                const send = pickSendClientContent(sess);
                sendTextRef.current = send;
                if (!send) {
                  setErrorMessage('Live session could not send messages.');
                  setPhase('error');
                  connectLockRef.current = false;
                  return;
                }
                try {
                  const startPayload = buildPictureTalkStartEvent(scene);
                  setLastSent('EVENT: START');
                  await send(startPayload);
                  // Do NOT auto-send OBJECT_CLICKED here. OBJECT_CLICKED events are sent only
                  // when the student explicitly taps a hotspot (via notifyItemClicked -> flushPendingObjectClick).
                  setIsReady(true);
                } catch {
                  setErrorMessage('Failed to start tutor session.');
                  setPhase('error');
                  connectLockRef.current = false;
                }

                const inCtx = inputAudioContextRef.current;
                const media = streamRef.current;
                if (!inCtx || !media || generationRef.current !== gen) return;

                const source = inCtx.createMediaStreamSource(media);
                inputSourceRef.current = source;
                const processor = inCtx.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
                processorRef.current = processor;

                processor.onaudioprocess = (e) => {
                  if (generationRef.current !== gen) return;
                  if (isMutedRef.current) {
                    clientSilenceMsRef.current = 0;
                    return;
                  }
                  if (isTeacherSpeakingRef.current) {
                    clientSilenceMsRef.current = 0;
                    clientVadEndSentRef.current = false;
                    return;
                  }
                  const inputData = e.inputBuffer.getChannelData(0);
                  const rms = Math.sqrt(inputData.reduce((s, v) => s + v * v, 0) / inputData.length);
                  const pcmBlob = createBlob(inputData);
                  void sp.then((s: unknown) => {
                    if (generationRef.current !== gen || isMutedRef.current || isTeacherSpeakingRef.current) return;
                    const live = s as { sendRealtimeInput?: (p: unknown) => Promise<void> };
                    live.sendRealtimeInput?.({ media: pcmBlob }).catch(() => {});
                  }).catch(() => {});

                  if (!didOpenRef.current) return;
                  const bufferMs = (inputData.length / INPUT_AUDIO_CONTEXT_RATE) * 1000;
                  if (rms < CLIENT_VAD_SILENCE_RMS) {
                    clientSilenceMsRef.current += bufferMs;
                    if (clientSilenceMsRef.current >= CLIENT_VAD_SILENCE_MS && !clientVadEndSentRef.current) {
                      clientVadEndSentRef.current = true;
                      void sp.then((s: unknown) => {
                        if (generationRef.current !== gen || isMutedRef.current || isTeacherSpeakingRef.current) return;
                        const live = s as { sendRealtimeInput?: (p: unknown) => Promise<void> };
                        live.sendRealtimeInput?.({ audioStreamEnd: true }).catch(() => {});
                      }).catch(() => {});
                    }
                  } else {
                    clientSilenceMsRef.current = 0;
                    clientVadEndSentRef.current = false;
                  }
                };

                source.connect(processor);
                processor.connect(inCtx.destination);
              });
            },
            onmessage: async (message: LiveServerMessage) => {
              if (generationRef.current !== gen) return;
              const sc = message?.serverContent as Record<string, unknown> | undefined;
              const outTx = sc?.outputTranscription ?? sc?.output_transcription;
              if (outTx && typeof outTx === 'object') {
                const tx = outTx as { text?: string; transcript?: string };
                const t = String(tx.text ?? tx.transcript ?? '').trim();
                if (t) setLastReply(t);
              }
              const parts = (sc?.modelTurn as { parts?: unknown[] } | undefined)?.parts || [];
              const textParts = parts.filter(
                (x: unknown) => typeof (x as { text?: string })?.text === 'string' && String((x as { text: string }).text).trim()
              ) as { text: string }[];
              textParts.forEach((x) => {
                const t = x.text.trim();
                if (t) setLastModelText((prev) => (prev ? `${prev}\n${t}` : t));
              });

              const p = parts.find((x: unknown) => (x as { inlineData?: { data?: string } })?.inlineData?.data);
              const base64Audio = (p as { inlineData?: { data?: string } } | undefined)?.inlineData?.data;
              if (!base64Audio) {
                if (sc?.interrupted) interruptOutput();
                return;
              }

              isTeacherSpeakingRef.current = true;
              const audioCtx = audioContextRef.current;
              if (!audioCtx) return;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, audioCtx.currentTime);
              const buffer = await decodeAudioData(decode(base64Audio), audioCtx, OUTPUT_SAMPLE_RATE, 1);
              const src = audioCtx.createBufferSource();
              src.buffer = buffer;
              src.connect(audioCtx.destination);
              src.onended = () => {
                sourcesRef.current.delete(src);
                if (sourcesRef.current.size === 0) isTeacherSpeakingRef.current = false;
              };
              src.start(nextStartTimeRef.current);
              nextStartTimeRef.current += buffer.duration;
              sourcesRef.current.add(src);
            },
            onerror: (err: unknown) => {
              if (generationRef.current !== gen) return;
              const msg = err instanceof Error ? err.message : String(err ?? 'Live error');
              setErrorMessage(msg);
              setPhase('error');
              void cleanupAudioAndMic();
              connectLockRef.current = false;
            },
            onclose: () => {
              if (generationRef.current !== gen) return;
              void cleanupAudioAndMic();
              connectLockRef.current = false;
              if (intentionalEndRef.current) {
                intentionalEndRef.current = false;
                setPhase('ended');
              } else if (didOpenRef.current) {
                setPhase('ended');
              } else {
                setErrorMessage('Connection closed before ready.');
                setPhase('error');
              }
            },
          },
        });

        sessionPromiseRef.current = sp;
        sp.catch((e: unknown) => {
          if (generationRef.current !== gen) return;
          if (isSchoolFeatureDisabledHandledError(e) || isAuthSessionExpiredHandledError(e)) {
            setPhase('idle');
            connectLockRef.current = false;
            return;
          }
          setErrorMessage(e instanceof Error ? e.message : 'Live connect failed.');
          setPhase('error');
          connectLockRef.current = false;
        });
      } catch (e: unknown) {
        if (isSchoolFeatureDisabledHandledError(e) || isAuthSessionExpiredHandledError(e)) {
          setPhase('idle');
        } else {
          setErrorMessage(e instanceof Error ? e.message : 'Failed to start conversation.');
          setPhase('error');
        }
        await cleanupAudioAndMic();
        connectLockRef.current = false;
      }
    },
    [navigate, refetchFeatureAccess, cleanupAudioAndMic, interruptOutput, dialectKey, studentGrade]
  );

  const resetToIdle = useCallback(async () => {
    await endConversation();
    setPhase('idle');
    setErrorMessage(null);
    setLastSent('');
    setLastReply('');
    setLastModelText('');
  }, [endConversation]);

  return {
    phase,
    isReady,
    error: errorMessage,
    lastSent,
    lastReply,
    lastModelText,
    startConversation,
    endConversation,
    resetToIdle,
    notifyItemClicked,
  };
}
